import { observer } from 'mobx-react-lite';
import { Box, Card, CardProps, Page } from '@wix/design-system';
import React, { ReactElement, ReactNode, useEffect } from 'react';
import { CollectionListHeader } from '../CollectionListHeader';
import {
  CollectionToolbar,
  CollectionToolbarProps,
} from '../CollectionToolbar';
import { TransitionGroup } from 'react-transition-group';
import { CollectionListRow } from '../CollectionListRow';
import { CollectionListItemProps } from '../CollectionListItem';
import { FiltersMap } from '@wix/bex-core';
import { ToolbarCollectionState } from '../../state';

export interface CollectionListProps<T, F extends FiltersMap = FiltersMap>
  extends Partial<CardProps> {
  state: ToolbarCollectionState<T, F>;
  search?: CollectionToolbarProps<T, F>['search'];
  title?: CollectionToolbarProps<T, F>['title'];
  showTotal?: CollectionToolbarProps<T, F>['showTotal'];
  filters?: CollectionToolbarProps<T, F>['filters'];
  stickyHeader?: boolean;
  renderItem: (item: T, index: number) => ReactElement<CollectionListItemProps>;
  renderError?: (err: unknown) => ReactNode;
  renderEmptyState?: () => ReactNode;
}

function _CollectionList<T, F extends FiltersMap = FiltersMap>({
  state,
  renderItem,
  search,
  title,
  showTotal,
  filters,
  renderError,
  renderEmptyState,
  stickyHeader,
  children,
  ...rest
}: CollectionListProps<T, F>) {
  const { collection } = state;
  const {
    result: { keyedItems, status },
  } = collection;

  useEffect(() => {
    return state.init({
      featuresInitializers: [],
    });
  }, []);

  let child: ReactNode;

  if (status.isError && !keyedItems.length) {
    child = renderError && (
      <Box backgroundColor="D80" padding="large">
        {renderError(status.error)}
      </Box>
    );
  } else if (status.isSuccess && !keyedItems.length) {
    child = renderEmptyState && (
      <Box backgroundColor="D80" padding="large">
        {renderEmptyState()}
      </Box>
    );
  } else {
    const listItems = keyedItems.map((keyedItem, index) => {
      const { key, item } = keyedItem;
      const element = renderItem(item, index);

      return (
        <CollectionListRow
          dataHook="collection-list-row"
          fade
          element={element}
          key={key}
          keyedItem={keyedItem}
          collection={state.collection}
        />
      );
    });

    child = <TransitionGroup>{listItems}</TransitionGroup>;
  }

  const [firstItem] = keyedItems;

  const toolbarAndHeader = (
    <>
      <CollectionToolbar
        dataHook="collection-toolbar"
        state={state}
        search={search}
        filters={filters}
        showTotal={showTotal}
        title={title}
      />
      {firstItem && (
        <CollectionListHeader element={renderItem(firstItem.item, 0)} />
      )}
    </>
  );

  return (
    <Card {...rest}>
      {stickyHeader ? (
        <Page.Sticky>{toolbarAndHeader}</Page.Sticky>
      ) : (
        toolbarAndHeader
      )}
      {child}
      {children}
    </Card>
  );
}

export const CollectionList = observer(_CollectionList);
